'use client'

import { createContext, useContext, type ReactNode } from 'react'

import { CreationDetail } from '@/lib/api'

interface WorkContextValue {
  /**
   * 当前作品的详细信息
   */
  data?: CreationDetail
  /**
   * 作品列表（用于移动端滑动）
   */
  list?: CreationDetail[]
  /**
   * 初始索引（用于移动端滑动）
   */
  initialIndex?: number
  /**
   * 是否有数据
   */
  hasData?: boolean
  /**
   * 设置是否跳过重置 store（用于跳转到 space 页面时）
   */
  setSkipReset?: (skip: boolean) => void
}

const WorkContext = createContext<WorkContextValue | undefined>(undefined)

interface WorkProviderProps {
  children: ReactNode
  value: WorkContextValue
}

/**
 * Work Context Provider
 * 提供作品相关的数据给所有子组件
 */
export const WorkProvider = ({ children, value }: WorkProviderProps) => {
  return <WorkContext.Provider value={value}>{children}</WorkContext.Provider>
}

/**
 * 使用 Work Context 的 Hook
 *
 * @example
 * ```tsx
 * const { data, list, initialIndex } = useWorkContext()
 * ```
 */
export const useWorkContext = () => {
  const context = useContext(WorkContext)
  if (context === undefined) {
    throw new Error('useWorkContext must be used within a WorkProvider')
  }
  return context
}

/**
 * 使用当前作品数据的 Hook
 * 这是一个便捷的 hook，直接返回当前作品的详细信息
 *
 * @example
 * ```tsx
 * const data = useCurrentWork()
 * ```
 */
export const useCurrentWork = () => {
  const { data } = useWorkContext()
  return data
}
